import _ from 'lodash';

import { CoreEnums } from '@/utils/domain/constants';
import env from '@/config/env';
import CoreFormula from '../../formula';
import BaseFieldHandler from '../BaseFieldHandler';
import { tableWithCalculatedColumns } from '../utils';

const { FormFieldType, FormFieldStringType, FormTableMode } = CoreEnums;

export default class CalculatedFieldHandler extends BaseFieldHandler {
  static supportedTypes = [
    FormFieldStringType.CalculatedByFormula,
    FormFieldType.CalculatedByFormula,
    FormFieldStringType.AutoCalculatedByTableColumn,
    FormFieldType.AutoCalculatedByTableColumn,
    FormFieldStringType.Table,
    FormFieldType.Table,
  ];

  handle() {
    const { type } = this.baseConfig;

    const handlers = {
      [FormFieldStringType.CalculatedByFormula]: () => this.handleCalculatedByFormula(),
      [FormFieldType.CalculatedByFormula]: () => this.handleCalculatedByFormula(),
      [FormFieldStringType.AutoCalculatedByTableColumn]: () =>
        this.handleAutoCalculatedByTableColumn(),
      [FormFieldType.AutoCalculatedByTableColumn]: () => this.handleAutoCalculatedByTableColumn(),
      [FormFieldStringType.Table]: () => this.handleTable(),
      [FormFieldType.Table]: () => this.handleTable(),
    };

    const handler = handlers[type];
    if (!handler) {
      throw new Error(`Unsupported calculated field type: ${type}`);
    }

    return handler();
  }

  handleCalculatedByFormula() {
    const config = this.getBaseConfig();
    const { isDetails } = this.fieldState;
    const { format } = this.baseConfig;
    const deeps = this.getDependencies();

    const withCustomFormat = isDetails && config.value && !_.isNaN(config.value);

    return {
      ...config,
      format,
      deeps,
      disabled: true,
      ariaDisabled: true,
      ariaReadonly: true,
      ...(withCustomFormat && {
        value: CoreFormula.applyNumberFormat(config.value, format || env.NUMERIC_DEFAULT_FORMAT),
      }),
    };
  }

  handleAutoCalculatedByTableColumn() {
    const config = this.getBaseConfig();
    const { isDetails, isLocked } = this.fieldState;
    const {
      separator,
      mathOperation,
      filters,
      sourceField,
      sourceTable,
      decimalPlaces,
      maxDecimalValue,
      maxIntValue,
      maxLength,
      minDecimalValue,
      minIntValue,
      format,
      formId,
    } = this.baseConfig;

    return {
      ...config,
      separator,
      mathOperation,
      maxDecimalPlaces: decimalPlaces,
      maxDecimalValue,
      maxIntValue,
      maxLength,
      minDecimalValue,
      minIntValue,
      format,
      formId,
      disabled: true,
      ariaDisabled: true,
      ariaReadonly: true,
      ...(!isLocked &&
        !isDetails && {
          fieldFilters: filters,
          sourceAutoCalculatedTableField: sourceField?.internalName,
          sourceAutoCalculatedTable: sourceTable?.internalName,
        }),
    };
  }

  handleTable() {
    const config = this.getBaseConfig();
    const { isDetails } = this.fieldState;
    const {
      mathOperation,
      itemsCount,
      minTableLines,
      maxTableLines,
      id,
      formMode,
      sortMode,
      ...more
    } = this.baseConfig;

    return {
      ...config,
      type: 'table',
      mathOperation,
      inlineMode: formMode === FormTableMode.Inline,
      sortMode,
      itemsCount,
      ...(minTableLines > 1 && { minTableLines }),
      ...(maxTableLines > 1 && { maxTableLines }),
      ...(!isDetails && {
        withCalculatedColumns: tableWithCalculatedColumns(id, this.configData),
      }),
    };
  }
}
